export interface ShowIf {
  field?: string;
  op: 'eq' | 'neq' | 'in' | 'and';
  value?: string | string[];
  conditions?: ShowIf[];
}

export interface FieldOption {
  value: string;
  label: string;
  group?: string; // optional tag used by optionsGroupedBy filtering on a dependent field
  compatibleWith?: string[]; // optional array of medication values this option can be combined with
}

export interface FieldValidation {
  maxLength?: number;
  dynamicMaxLength?: Array<{ prefix: string; maxLength: number }>;
  autoPrefix?: Array<{ trigger: string; insert: string }>;
  maxFiles?: number;
  maxSizeBytes?: number;
  pattern?: string;
  patternMessage?: string;
  min?: string;        // date fields only: ISO date lower bound (inclusive), e.g. "1900-01-01"
  minMessage?: string; // inline error copy shown when the value is before min
}

export type FieldType =
  | 'hidden'
  | 'statement'
  | 'text'
  | 'email'
  | 'tel'
  | 'date'
  | 'radio'
  | 'select'
  | 'multiselect'
  | 'textarea'
  | 'address'
  | 'checkbox'
  | 'file';

export interface FieldSchema {
  id: string;
  label?: string;
  type: FieldType;
  required?: boolean;
  defaultValue?: unknown;
  helpText?: string;
  options?: FieldOption[];
  // select | multiselect only: id of another field whose current value filters this
  // field's options to those with a matching FieldOption.group. The field is disabled
  // until the controlling field has a value, and its own value resets when the
  // controlling field's value changes.
  optionsGroupedBy?: string;
  // select | multiselect only: id of another field (e.g., primary medication) whose
  // selected option's compatibleWith array filters this field's options. Used for
  // combination therapy to show only compatible medications.
  filterCompatibleWith?: string;
  validation?: FieldValidation;
  showIf?: ShowIf;
  placeholder?: string;      // text | tel | textarea only: hint text shown inside the input
  checkedValue?: string;    // checkbox only: value submitted when checked
  uncheckedValue?: string;  // checkbox only: value submitted when unchecked; omit to exclude field from payload
  checkboxLabel?: string;   // checkbox only: text rendered inline next to the checkbox
  showLabel?: boolean;      // defaults to true; false hides the heading above the control
  radioStyle?: 'inline' | 'boxed'; // radio only: 'inline' (default) or boxed card layout
  span?: number;            // grid columns to span within a multi-column section; defaults to 1
  maxFiles?: number;        // file fields outside form validation blocks, such as status uploads
  maxSizeBytes?: number;
  acceptedFileTypes?: string[]; // file fields only: MIME types the picker accepts (application/pdf, image/jpeg, image/png)
  maxCharLength?: number;   // textarea only: maximum character length for text input
  sensitive?: true;
}

export type StepAction = 'validateEligibility' | 'submit';

export interface SectionSchema {
  id: string;
  title: string;
  description?: string;
  columns?: number;  // number of grid columns; defaults to 1 (single-column stack)
  showIf?: ShowIf;
  fields: FieldSchema[];
}

export interface AuthPageConfig {
  showSupportInfo?: boolean;
  title?: string;    // heading and tab title on auth pages; overrides theme.displayName
  logoUrl?: string;  // logo on auth pages; overrides theme.logoUrl
}

export interface LandingFeatureConfig {
  label?: string;
  labelSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string;  // Mantine text size token ("xs"–"xl") or any CSS value e.g. "1.5rem", "24px"; defaults to 'lg'
  description?: string;
  color?: string;            // CSS color for the card label text; defaults to primaryColor
  icon?: string;             // key into the landing icon registry (e.g. "clipboard-plus", "search")
  iconColor?: string;        // CSS color for the icon; defaults to color, then primaryColor
  iconVariant?: 'plain' | 'filled';  // 'filled' = white icon on iconColor rounded bg; default 'plain'
  backgroundColor?: string;  // CSS color applied to the card background (e.g. "#E0F5EE")
  buttonLabel?: string;      // CTA button text; omit to hide button
  buttonVariant?: 'filled' | 'outline';  // 'filled' = primary color bg; 'outline' = white bg, dark text
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';  // Mantine button size; defaults to 'md'
  buttonWidth?: string;      // CSS width value e.g. "auto", "100%", "200px"; defaults to "100%"
  buttonHoverColor?: string; // background color on hover; defaults to primaryColor (filled) or transparent (outline)
}

export interface LandingDraftsConfig {
  title?: string;        // defaults to "Saved Drafts"
  description?: string;  // optional subtitle line
}

export interface LandingDestination {
  type: 'program-root';
  client: string;
  program: string;
}

export interface LandingProductConfig {
  productKey: string;
  title: string;
  logoUrl: string;
  logoAlt: string;
  accentColor: string;
  buttonLabel: string;
}

export interface LandingActionConfig {
  label: string;
  destination: LandingDestination;
}

export interface LandingPageConfig {
  title?: string;          // headline; overrides theme.displayName
  subtitle?: string;       // descriptive text below headline
  logoUrl?: string;        // overrides theme.logoUrl
  logoAlt?: string;
  products?: LandingProductConfig[];
  loginAction?: LandingActionConfig;
  legalText?: string;
  showSupportInfo?: boolean;
  enrollments?: LandingFeatureConfig;
  escalations?: LandingFeatureConfig;
  escalationsHistory?: LandingFeatureConfig;
  drafts?: LandingDraftsConfig;
}

export interface FormPageConfig {
  submitLabel?: string;
  action?: StepAction;
  showSupportInfo?: boolean;
  sections: SectionSchema[];
}

export interface ResultPageConfig {
  title?: string;
  message?: string;
  showSupportInfo?: boolean;
}

export interface PagesConfig {
  eyebrow?: string;
  title?: string;  // heading shown in the program hero section; overrides theme.displayName
  subtitle?: string;
  form: FormPageConfig[];
  success?: ResultPageConfig;
  alreadyEnrolled?: ResultPageConfig;
  ineligible?: ResultPageConfig;
  rejected?: ResultPageConfig;
  status?: EscalationStatusPageConfig;
  history?: EscalationHistoryPageConfig;
}

export interface EscalationStatusFieldConfig {
  path: string;
  previewPath?: string;
  label: string;
  omitIfEmpty?: boolean;
  span?: boolean;
  format?: string;
}

export interface ActivitySectionConfig {
  fields: string[];
  whenDateOnly?: boolean;
}

export interface InboundCallSectionConfig {
  title: string;
  description?: string;
  reporterNameField?: FieldSchema;
  noteField?: FieldSchema;
  uploadFields?: FieldSchema[];
  submitLabel: string;
  mockSubmission?: boolean;
}

// Top-level patientUpload block of the patient-upload-portal solution, served at /p: the
// login-free patient document upload page (ESCIQ-198, upload and confirmation in ESCIQ-196).
export interface PatientUploadConfirmationConfig {
  title: string;
  messageWithReceipt: string;
  messageWithoutReceipt?: string;
  referenceLabel: string;
  submitAnotherLabel: string;
}

export interface PatientUploadPageConfig {
  title: string;
  subtitle?: string;
  disclaimer?: string;
  cardIdField: FieldSchema;
  documentsField: FieldSchema;
  browseLabel?: string;
  receiptOptInLabel: string;
  confirmation: PatientUploadConfirmationConfig;
  submitLabel: string;
  mockSubmission?: boolean;
}

export interface EscalationStatusSectionsConfig {
  headers: EscalationStatusFieldConfig[];
  details: EscalationStatusFieldConfig[];
  insurance?: EscalationStatusFieldConfig[];
  outcome: boolean;
  segmentation: boolean;
  activity?: ActivitySectionConfig;
  submittedBy: boolean;
  inboundCall?: InboundCallSectionConfig;
}

export interface EscalationStatusPageConfig {
  detailsLayoutStyle?: string;
  sections: EscalationStatusSectionsConfig;
}

export interface EscalationHistoryPageConfig {
  searchPlaceholder?: string;
  columns: EscalationStatusFieldConfig[];
  searchableFields: Array<{
    key: string;
    label?: string;
    source?: 'raw_request' | 'result';
    path?: string[];
    column?: string;
  } | string>;
  filters?: Array<{
    field: string;
    label: string;
  }>;
}

export interface ThemeColors {
  portalName?: string;  // top-bar label color (e.g. "Patient Escalation Portal")
  title?: string;       // program name heading color in the hero
  subtitle?: string;    // subtitle / muted text color below the heading
  accent?: string;      // section titles, kickers, badges; falls back to primaryColor
  stepper?: string;     // progress tracker step color; falls back to accent → primaryColor
  background?: string;  // page background color; falls back to the default page background
}

export interface ThemeConfig {
  primaryColor: string;
  logoUrl?: string;
  faviconUrl?: string;
  displayName: string;
  uiStyle?: 'utilitarian' | 'lovable';  // defaults to 'utilitarian'
  showTopBarLogo?: boolean;
  showTopBarPortalName?: boolean;
  topBarLogoUrl?: string;
  topBarLogoAlt?: string;
  topBarLogoWidth?: number;
  topBarLogoHeight?: number;
  showFooterLogo?: boolean;
  footerLogoUrl?: string;
  showNotFoundLogo?: boolean;
  notFoundLogoUrl?: string;
  portalName?: string;      // optional: text shown in the sticky top bar (lovable only); defaults to 'RIS Rx Portal'
  titleAlign?: 'left' | 'center';  // alignment of the title in the program hero section; defaults to 'center'
  footerTagline?: string;   // optional text shown on the right side of the footer
  footerLegalText?: string; // optional legal text shown at the bottom of the footer
  prescribingInformationUrl?: string; // optional URL linked from the prescribing information footer line
  feedbackUrl?: string;     // optional URL shown as a feedback link in the footer
  contentSurface?: boolean; // enables the supported Copay or Voucher page content surface
  colors?: ThemeColors;     // optional per-slot text color overrides
}

export interface AuthConfig {
  allowedDomains: string[];
  allowedEmails: string[];
}

export interface TermsAndConditionsConfig {
  enabled: boolean;
  version: string;
  contentPath: string;
  requireAcceptance?: boolean;
  title?: string;
  backgroundColor?: string;
}

export interface SSOConfig {
  provider: string;
  issuer: string;
  authorizeUrl: string;
  tokenUrl: string;
  clientId: string;
  scopes: string[];
}

export interface SubmissionSupportConfig {
  intro?: string;
  name?: string;
  phone?: string;
  email?: string;
  details?: string[];
}

export type SiteType = 'enrollments' | 'escalations';

// Defines optional features within an EscalationIQ program.
export interface EscalationCapabilities {
  // Shows the Yours/All toggle on the history page for non-superuser sessions.
  // Superusers always see the toggle regardless of this flag.
  viewAllEnabled?: boolean;
  // Restricts the "All" view to records sharing at least one SSO group with the current user.
  // Only relevant when viewAllEnabled is true. When true and the user has no groups,
  // "All" behaves identically to "Yours".
  viewAllGroupScoped?: boolean;
  // Enables the "New Escalation Details" section on escalation status pages, allowing users to
  // log patient/stakeholder calls with details and documentation. Requires backend
  // integration (BENIQ-4264) for persistence.
  inboundCallLoggingEnabled?: boolean;
  // Lets BenIQ ask EscalationIQ to send this program's patients a patient upload portal link
  // (ESCIQ-151). Enforced by the backend; the SPA does not use it.
  patientPortalEnabled?: boolean;
}

interface ProgramConfigBase {
  clientName: string;
  programName: string;
  authenticationRequired: boolean;
  theme: ThemeConfig;
  support?: SubmissionSupportConfig;
  magicLinksEnabled?: boolean;
  sso?: SSOConfig;
  termsAndConditions?: TermsAndConditionsConfig;
  pages?: { login?: AuthPageConfig; verify?: AuthPageConfig; landing?: LandingPageConfig };
}

export type Solution =
  | 'copay-enrollment'
  | 'copay-enrollment-and-bi-escalation'
  | 'bi-escalation'
  | 'escalationiq'
  | 'multi-product-copay-enrollment'
  | 'multi-product-voucher-submission'
  | 'voucher-participation-request'
  | 'patient-upload-portal';

interface EnrollmentSection {
  pages: PagesConfig;
}

interface EscalationSection {
  pages: PagesConfig;
  capabilities?: EscalationCapabilities;
}

interface VoucherParticipationRequestSection {
  pages: {
    title?: string;
    subtitle?: string;
    requiredFieldsNote?: string;
    success: {
      title: string;
      message: string;
    };
    failure: {
      message: string;
    };
    notificationFailure: {
      message: string;
    };
    form?: Array<{ sections: SectionSchema[] }>;
  };
}

// Raw response from GET /api/v1/:client/:program/config.
export type ProgramConfig = ProgramConfigBase & (
  | {
      solution: 'copay-enrollment';
      enrollments: EnrollmentSection;
      escalations?: never;
      products?: never;
      voucherSubmission?: never;
      voucherParticipationRequest?: never;
    }
  | {
      solution: 'copay-enrollment-and-bi-escalation';
      enrollments: EnrollmentSection;
      escalations: EscalationSection;
      products?: never;
      voucherSubmission?: never;
      voucherParticipationRequest?: never;
    }
  | {
      solution: 'bi-escalation';
      enrollments?: never;
      escalations: EscalationSection;
      products?: never;
      voucherSubmission?: never;
      voucherParticipationRequest?: never;
    }
  | {
      solution: 'escalationiq';
      enrollments?: EnrollmentSection;
      escalations: EscalationSection;
      products?: never;
      voucherSubmission?: never;
      voucherParticipationRequest?: never;
    }
  | {
      solution: 'multi-product-copay-enrollment';
      enrollments?: never;
      escalations?: never;
      products: Record<string, EnrollmentProductConfig>;
      voucherSubmission?: never;
      voucherParticipationRequest?: never;
    }
  | {
      solution: 'multi-product-voucher-submission';
      enrollments?: never;
      escalations?: never;
      products: Record<string, EnrollmentProductConfig>;
      voucherSubmission: VoucherSubmissionConfig;
      voucherParticipationRequest?: never;
    }
  | {
      solution: 'voucher-participation-request';
      enrollments?: never;
      escalations?: never;
      products?: never;
      voucherSubmission?: never;
      voucherParticipationRequest: VoucherParticipationRequestSection;
    }
  | {
      solution: 'patient-upload-portal';
      enrollments?: never;
      escalations?: never;
      products?: never;
      voucherSubmission?: never;
      voucherParticipationRequest?: never;
      patientUpload: PatientUploadPageConfig;
    }
);

export interface EnrollmentProductConfig {
  theme?: ThemeConfig;
  pages: PagesConfig;
}

export interface VoucherDocumentFieldConfig {
  fieldId: string;
  type: 'PROOF_OF_PURCHASE' | 'PA_DENIAL' | 'SUPPORTING';
  required: boolean;
}

export interface VoucherDocumentsConfig {
  productSelectorFieldId: string;
  documentFields: VoucherDocumentFieldConfig[];
  pages: PagesConfig;
}

export interface VoucherSubmissionConfig {
  documents: VoucherDocumentsConfig;
}

// Narrows ProgramConfig to a specific siteType with pages guaranteed non-null.
// Form components use this so they can access config.pages directly without assertions.
// Auth pages that don't need pages can continue using ProgramConfig.
export type SiteConfig = ProgramConfig & {
  siteType: SiteType;
  pages: PagesConfig;
};

export function toSiteConfig(config: ProgramConfig, siteType: SiteType): SiteConfig | null {
  const section = config[siteType];
  if (!section) return null;
  return { ...config, siteType, pages: section.pages };
}


